<template>
  <modal
    name="crew-import"
    :width="1040"
    height="auto"
    :adaptive="true"
    :scrollable="true"
    class="modal-not-overflow"
    @before-open="onBeforeOpen"
    @closed="reset"
  >
    <section class="crew-import" aria-labelledby="crew-import-title">
      <app-modal-header
        design="new"
        eyebrow="Mountain crew"
        :title="title"
        title-id="crew-import-title"
        @close="close"
      />

      <div v-if="loading" class="crew-import__body" role="status" aria-label="Loading import" aria-busy="true">
        <span v-for="row in 5" :key="row" class="crew-import__skeleton" aria-hidden="true" />
      </div>

      <form v-else-if="step === 'upload'" class="crew-import__body" @submit.prevent="upload">
        <app-form-group design="new" label="What should the file do?">
          <app-filters v-model="mode" design="new" name="crew-import-mode" :filters="modes" />
          <p class="crew-import__hint">{{ modeHint }}</p>
        </app-form-group>

        <app-form-group design="new" label="Excel file" label-for="crew-import-file" required>
          <label class="crew-import__drop" :class="{ 'crew-import__drop--filled': file }">
            <input id="crew-import-file" type="file" accept=".xlsx" @change="selectFile">
            <strong>{{ file ? file.name : 'Choose the .xlsx file' }}</strong>
            <span>{{ file ? fileSize : 'Crew data on the "Database" sheet · up to 25 MB' }}</span>
          </label>
        </app-form-group>

        <p v-if="notice" class="crew-import__notice">{{ notice }}</p>
        <p v-if="error" class="crew-import__error" role="alert">{{ error }}</p>

        <footer class="crew-import__footer">
          <app-button design="new" theme="dark" @click.native="close">Cancel</app-button>
          <app-button ref="uploadButton" design="new" type="submit" :disabled="!file">{{ uploadLabel }}</app-button>
        </footer>
      </form>

      <div v-else-if="step === 'reading'" class="crew-import__body" role="status" aria-live="polite">
        <div class="crew-import__reading">
          <span class="crew-import__spinner" aria-hidden="true" />
          <div>
            <strong>{{ crewImport.file_name }}</strong>
            <span>{{ crewImport.phase || 'Queued' }}</span>
          </div>
        </div>
        <p class="crew-import__hint">
          The file is read in the background, so this can take a while for a big workbook. The columns appear here when it is
          ready. You can close this window: the import stays in progress and can be continued from the Crew page.
        </p>
        <p v-if="error" class="crew-import__error" role="alert">{{ error }}</p>
        <footer class="crew-import__footer">
          <app-button ref="cancelReadingButton" design="new" theme="dark" @click.native="cancelReading">Cancel import</app-button>
          <app-button design="new" @click.native="close">Close</app-button>
        </footer>
      </div>

      <div v-else class="crew-import__body">
        <div class="crew-import__summary">
          <div>
            <strong>{{ crewImport.file_name }}</strong>
            <span>{{ columnCountText }}</span>
          </div>
        </div>

        <p v-if="crewImport.error_message" class="crew-import__error" role="alert">{{ crewImport.error_message }}</p>
        <p v-if="notice" class="crew-import__notice">{{ notice }}</p>
        <p v-for="warning in crewImport.warnings" :key="warning.code" class="crew-import__notice crew-import__notice--amber">
          {{ warning.message }}
        </p>

        <app-data-table
          design="new"
          :columns="columns"
          :rows="columnRows"
          row-key="index"
          min-width="760px"
        >
          <template #cell-column="{ row }">
            <div class="crew-import__column">
              <span class="crew-import__letter">{{ row.letter }}</span>
              <span class="crew-import__header">{{ row.header || '(no title)' }}</span>
            </div>
          </template>
          <template #cell-samples="{ row }">
            <span v-if="row.samples.length" class="crew-import__samples">{{ row.samples.join(' · ') }}</span>
            <span v-else class="crew-import__muted">Empty</span>
          </template>
          <template #cell-field="{ row }">
            <app-select
              design="new"
              :aria-label="`Field for column ${row.letter}`"
              :value="optionFor(row.index)"
              :options="fieldOptions"
              label="label"
              placeholder="Not imported"
              @input="setMapping(row.index, $event)"
            />
            <span v-if="ignoredReason(row.index) && !mapping[row.index]" class="crew-import__muted">
              {{ ignoredReason(row.index) }}
            </span>
          </template>
        </app-data-table>

        <ul v-if="problems.length" class="crew-import__problems" role="alert">
          <li v-for="problem in problems" :key="problem">{{ problem }}</li>
        </ul>
        <p v-if="error" class="crew-import__error" role="alert">{{ error }}</p>

        <footer class="crew-import__footer">
          <span class="crew-import__muted">{{ mappedCount }} of {{ crewImport.columns.length }} columns will be imported</span>
          <app-button design="new" theme="dark" @click.native="close">Close</app-button>
          <app-button ref="startButton" design="new" :disabled="problems.length > 0" @click.native="start">
            Build preview
          </app-button>
        </footer>
      </div>
    </section>
  </modal>
</template>

<script>
import {
  getClimbingMountainCrewImport,
  getClimbingMountainCrewImportFields,
  patchClimbingMountainCrewImportMapping,
  postClimbingMountainCrewImport,
  postClimbingMountainCrewImportCancel,
  postClimbingMountainCrewImportStart
} from '@/http'
import { apiErrorMessage } from '../importState'

const MAX_FILE_SIZE = 25 * 1024 * 1024
const POLL_INTERVAL = 1500
const MODES = [
  { value: 'partial', label: 'Partial update' },
  { value: 'full', label: 'Full registry' }
]
const MODE_HINTS = {
  partial: 'Only the crew in the file are compared and updated. Everyone else is left as they are.',
  full: 'The file is the whole registry. Crew missing from it are marked as outside the latest import; nobody is deleted or deactivated.'
}

export default {
  name: 'CrewImportModal',
  data() {
    return {
      step: 'upload',
      loading: false,
      mode: 'partial',
      modes: MODES,
      file: null,
      uploadPercent: null,
      pollTimer: null,
      error: '',
      crewImport: null,
      fields: [],
      notice: '',
      mapping: {},
      columns: [
        { key: 'column', label: 'Column', width: '220px' },
        { key: 'samples', label: 'Sample values', width: '260px' },
        { key: 'field', label: 'Import into', minWidth: '260px' }
      ]
    }
  },
  computed: {
    title() {
      if (this.step === 'upload') return 'Import crew from Excel'
      return this.step === 'reading' ? 'Reading the file' : 'Map the columns'
    },
    uploadLabel() {
      return this.uploadPercent === null ? 'Upload and map columns' : `Uploading ${this.uploadPercent}%`
    },
    modeHint() { return MODE_HINTS[this.mode] },
    fileSize() {
      if (!this.file) return ''
      if (this.file.size > 1024 * 1024) return `${(this.file.size / (1024 * 1024)).toFixed(1)} MB`
      return `${Math.max(1, Math.round(this.file.size / 1024))} KB`
    },
    columnRows() { return this.crewImport ? this.crewImport.columns : [] },
    columnCountText() {
      if (!this.crewImport) return ''
      return `${this.crewImport.columns.length} columns, header in row ${this.crewImport.header_row}`
    },
    fieldOptions() { return this.fields },
    mappedCount() { return Object.values(this.mapping).filter(Boolean).length },
    problems() {
      const used = {}
      const problems = []
      Object.entries(this.mapping).forEach(([index, key]) => {
        if (!key) return
        if (used[key]) {
          const label = (this.fields.find(field => field.key === key) || {}).label || key
          if (!problems.some(problem => problem.includes(`"${label}"`))) {
            problems.push(`"${label}" is chosen for more than one column.`)
          }
        }
        used[key] = index
      })
      if (this.crewImport && !used.old_id) problems.push('Choose the column for "External ID (#)": it is the key used to match existing crew.')
      return problems
    }
  },
  methods: {
    onBeforeOpen(event) {
      this.reset()
      const importId = event.params && event.params.importId
      this.loadFields()
      if (importId) this.resume(importId)
    },
    reset() {
      this.stopPolling()
      this.step = 'upload'
      this.loading = false
      this.mode = 'partial'
      this.file = null
      this.uploadPercent = null
      this.error = ''
      this.crewImport = null
      this.mapping = {}
    },
    close() {
      this.$modal.hide('crew-import')
    },
    loadFields() {
      getClimbingMountainCrewImportFields().then(response => {
        this.fields = response.data.importable
        this.notice = response.data.notice
      }).catch(() => {})
    },
    resume(importId) {
      this.loading = true
      getClimbingMountainCrewImport(importId).then(response => {
        this.handleImport(response.data)
      }).catch(error => {
        this.error = apiErrorMessage(error)
      }).finally(() => { this.loading = false })
    },
    selectFile(event) {
      const file = event.target.files[0] || null
      this.error = ''
      if (file && !file.name.toLowerCase().endsWith('.xlsx')) {
        this.file = null
        this.error = 'Only .xlsx files can be imported.'
        event.target.value = ''
        return
      }
      if (file && file.size > MAX_FILE_SIZE) {
        this.file = null
        this.error = 'The file is larger than 25 MB.'
        event.target.value = ''
        return
      }
      this.file = file
    },
    // The file is read by a background task: follow the import until it is ready for mapping.
    handleImport(data) {
      if (data.status === 'parsing') {
        this.crewImport = data
        this.step = 'reading'
        this.poll(data.id)
      } else if (['uploaded', 'mapped'].includes(data.status)) {
        this.showMapping(data)
      } else if (data.status === 'failed' && !data.columns.length) {
        this.step = 'upload'
        this.crewImport = null
        this.error = data.error_message || 'The file could not be read.'
      } else if (data.status === 'cancelled') {
        this.step = 'upload'
        this.crewImport = null
        this.error = 'The import was cancelled.'
      } else {
        this.close()
        this.$router.push({ name: 'climbing-mountain-crew-import-detail', params: { importId: data.id } }).catch(() => {})
      }
    },
    poll(importId) {
      this.stopPolling()
      this.pollTimer = window.setTimeout(() => {
        getClimbingMountainCrewImport(importId).then(response => {
          if (!this.isPolling(importId)) return
          this.handleImport(response.data)
        }).catch(() => {
          // a failed poll is not a failed import: keep waiting
          if (this.isPolling(importId)) this.poll(importId)
        })
      }, POLL_INTERVAL)
    },
    // false once the modal was closed or reopened for another import while the request was out
    isPolling(importId) {
      return this.pollTimer !== null && Boolean(this.crewImport) && this.crewImport.id === importId
    },
    stopPolling() {
      window.clearTimeout(this.pollTimer)
      this.pollTimer = null
    },
    cancelReading() {
      const button = this.$refs.cancelReadingButton
      this.error = ''
      if (button) button.preload = true
      postClimbingMountainCrewImportCancel(this.crewImport.id).then(() => {
        this.$notify({ type: 'success', title: 'The import was cancelled' })
        this.close()
      }).catch(error => {
        this.error = apiErrorMessage(error)
      }).finally(() => { if (button) button.preload = false })
    },
    upload() {
      if (!this.file) return
      const button = this.$refs.uploadButton
      const data = new FormData()
      data.append('file', this.file)
      data.append('mode', this.mode)
      this.error = ''
      this.uploadPercent = 0
      button.preload = true
      postClimbingMountainCrewImport(data, {
        onUploadProgress: event => {
          if (event.total) this.uploadPercent = Math.min(100, Math.round((event.loaded / event.total) * 100))
        }
      }).then(response => {
        this.handleImport(response.data)
      }).catch(error => {
        this.error = apiErrorMessage(error)
      }).finally(() => {
        this.uploadPercent = null
        button.preload = false
      })
    },
    showMapping(data) {
      this.crewImport = data
      this.mapping = { ...data.column_mapping }
      this.step = 'mapping'
    },
    optionFor(index) {
      const key = this.mapping[index]
      return key ? this.fields.find(field => field.key === key) || null : null
    },
    ignoredReason(index) {
      return this.crewImport && this.crewImport.ignored_columns[String(index)]
    },
    setMapping(index, option) {
      this.$set(this.mapping, String(index), option ? option.key : null)
    },
    async start() {
      const button = this.$refs.startButton
      const mapping = {}
      Object.entries(this.mapping).forEach(([index, key]) => { if (key) mapping[index] = key })
      this.error = ''
      button.preload = true
      try {
        await patchClimbingMountainCrewImportMapping(this.crewImport.id, { column_mapping: mapping })
        await postClimbingMountainCrewImportStart(this.crewImport.id)
        const importId = this.crewImport.id
        this.$notify({ type: 'success', title: 'The preview is being built' })
        this.close()
        this.$router.push({ name: 'climbing-mountain-crew-import-detail', params: { importId } }).catch(() => {})
      } catch (error) {
        this.error = apiErrorMessage(error)
      } finally {
        button.preload = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.crew-import {
  color: $ui-text;
  font-family: $ui-font-body;

  &__body {
    display: flex;
    flex-direction: column;
    gap: $ui-space-16;
    padding: $ui-space-20 $ui-space-24 $ui-space-24;
  }

  &__hint,
  &__muted {
    margin: $ui-space-8 0 0;
    color: $ui-muted;
    font-size: $ui-font-size-metadata;
    line-height: 1.5;
  }

  &__muted { margin: 0; display: block; }

  &__drop {
    display: flex;
    flex-direction: column;
    gap: $ui-space-4;
    padding: $ui-space-16 $ui-space-20;
    border: 1px dashed $ui-line-strong;
    border-radius: $ui-panel-radius;
    background-color: $ui-surface-soft;
    cursor: pointer;

    input { position: absolute; width: 1px; height: 1px; opacity: 0; }
    strong { color: $ui-ink; font-size: $ui-font-size-body; font-weight: 600; overflow-wrap: anywhere; }
    span { color: $ui-muted; font-size: $ui-font-size-metadata; }

    &:hover { border-color: $ui-blue; }
    &:focus-within { border-color: $ui-blue; outline: 2px solid $ui-blue; outline-offset: 2px; }
    &--filled { border-style: solid; background-color: $ui-blue-soft; border-color: $ui-blue; }
  }

  &__notice {
    margin: 0;
    padding: $ui-space-12 $ui-space-16;
    border: 1px solid $ui-line;
    border-radius: $ui-panel-radius;
    background-color: $ui-blue-soft;
    color: $ui-text;
    font-size: $ui-font-size-body;
    line-height: 1.45;

    &--amber { background-color: $ui-amber-soft; color: $ui-amber; }
  }

  &__error {
    margin: 0;
    padding: $ui-space-12 $ui-space-16;
    border: 1px solid $ui-red;
    border-radius: $ui-panel-radius;
    background-color: $ui-red-soft;
    color: $ui-red;
    font-size: $ui-font-size-body;
  }

  &__problems {
    margin: 0;
    padding: $ui-space-12 $ui-space-16 $ui-space-12 $ui-space-32;
    border: 1px solid $ui-amber;
    border-radius: $ui-panel-radius;
    background-color: $ui-amber-soft;
    color: $ui-amber;
    font-size: $ui-font-size-body;
  }

  &__summary {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: $ui-space-16;
    min-width: 0;

    > div:first-child { display: flex; flex-direction: column; gap: $ui-space-4; min-width: 0; }
    strong { color: $ui-ink; font-size: $ui-font-size-body; font-weight: 600; overflow-wrap: anywhere; }
    span { color: $ui-muted; font-size: $ui-font-size-metadata; }
  }

  &__column { display: flex; align-items: center; gap: $ui-space-12; min-width: 0; }
  &__letter {
    display: inline-flex;
    flex: 0 0 auto;
    min-width: 32px;
    height: 24px;
    align-items: center;
    justify-content: center;
    border-radius: $ui-control-radius;
    background-color: $ui-surface-soft;
    border: 1px solid $ui-line;
    color: $ui-muted;
    font-size: $ui-font-size-metadata;
    font-weight: 600;
  }
  &__header { color: $ui-ink; font-weight: 600; overflow-wrap: anywhere; }
  &__samples { color: $ui-text; overflow-wrap: anywhere; }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: $ui-space-12;

    .crew-import__muted { margin-right: auto; }
  }

  &__reading {
    display: flex;
    align-items: center;
    gap: $ui-space-16;
    padding: $ui-space-16;
    border: 1px solid $ui-line;
    border-radius: $ui-panel-radius;

    div { display: flex; flex-direction: column; gap: $ui-space-4; min-width: 0; }
    strong { color: $ui-ink; font-weight: 600; overflow-wrap: anywhere; }
    span { color: $ui-text; }
  }

  &__spinner {
    flex: none;
    width: 24px;
    height: 24px;
    border: 3px solid $ui-line;
    border-top-color: $ui-blue;
    border-radius: 50%;
    animation: crew-import-spin 0.9s linear infinite;
  }

  &__skeleton {
    display: block;
    height: 32px;
    border-radius: $ui-control-radius;
    background-color: $ui-line;
  }
}

@keyframes crew-import-spin {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .crew-import__spinner { animation-duration: 3s; }
}

@media (max-width: 600px) {
  .crew-import__body { padding: $ui-space-16; }
  .crew-import__summary { flex-direction: column; align-items: stretch; }
}
</style>
